import { Theme } from "@mui/material";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
const drawerWidth = 240;

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
    },
    menuButton: {
      marginRight: 36,
    },
    hide: {
      display: "none",
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: "nowrap",
      "& .MuiDrawer-paper": {
        // Sit below the 64px desktop top nav.
        top: 64,
        [theme.breakpoints.down('md')]: {
          // Mobile header is 80px.
          top: 80,
        },
        // Replace MUI's default rgba(0,0,0,0.12) right border with the
        // lavender tone used elsewhere (e.g. top nav borderBottom) so
        // the rail reads as part of the same frame as the top nav.
        // We render the border as a ::after pseudo so we can hide it
        // entirely when the drawer is collapsed (see drawerClose) and
        // let the standalone `sideRailRightBorder` element take over.
        // The standalone element sits at col 60, 1px outside the paper,
        // which is exactly where the cornerCurve's arc terminates.
        borderRight: "none",
        // Allow the menu list to scroll when it exceeds the viewport
        // (e.g. super_admin sees ~20 items on short screens) but hide
        // the scrollbar chrome to keep the rail visually clean.
        // Cross-browser: Firefox uses `scrollbar-width`, IE/legacy Edge
        // use `-ms-overflow-style`, Chromium/Safari hide via the
        // ::-webkit-scrollbar pseudo-element.
        overflowY: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          // Default (drawer expanded): full-height right border so the
          // expanded panel reads as a complete rectangle joining the
          // top nav border at its top-right corner.
          top: 0,
          right: 0,
          width: 1,
          bottom: 0,
          backgroundColor: "#DADFF6",
          pointerEvents: "none",
        },
      },
    },
    drawerOpen: {
      width: drawerWidth,
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerClose: {
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: "hidden",
      width: 60,
      // When collapsed, hide the in-paper border. The `sideRailRightBorder`
      // element sits at col 60 (1px right of the paper) and aligns with
      // the cornerCurve's bottom tangent — the paper's own ::after would
      // sit 1px to the left of that and create the visual offset the user
      // saw at the bottom of the curve.
      "&.MuiDrawer-paper::after": {
        display: "none",
      },
      [theme.breakpoints.down('md')]: {
        width: 0,
      },
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    },
    listItem: {
      "& .MuiListItemIcon-root.active": {
        color: "blue",
      },
      // Bold the label when this row is the active menu item. Targets the
      // `active` class we add to ListItemText (see AdminSidebar render).
      "& .MuiListItemText-root.active .MuiListItemText-primary": {
        fontWeight: 700,
      },
    },
    // Decorative quarter-circle that softens the right-angle where the
    // fixed top nav (h: 64, borderBottom #DADFF6) meets the collapsed
    // side rail (w: 60). Stays at the collapsed corner; the Drawer
    // paper (zIndex 1200) slides over it on hover-expand.
    //
    // The 16x16 footprint is split by a quarter-circle arc centered on
    // the bottom-right corner (radius 16):
    //   - Top-left triangle (OUTSIDE the arc): opaque white. This is the
    //     inner corner of the nav L-shape — it masks the cut-off ends of
    //     the top nav and side-rail borders, and prevents any underlying
    //     DOM from bleeding through.
    //   - 1px ring at radius 16: #DADFF6, drawing the visible curve.
    //   - Bottom-right wedge (INSIDE the arc): transparent. This is the
    //     content area — letting full-bleed page chrome (e.g. the
    //     dashboard hero gradient) show through with a soft rounded
    //     inner corner.
    cornerCurve: {
      position: "fixed",
      // The 17x17 box is anchored 1px above + 1px left of the visual
      // corner so the gradient's tangent points sit fully inside the box
      // (avoids anti-aliasing chopping the arc's last pixel).
      top: 63,
      left: 59,
      width: 17,
      height: 17,
      pointerEvents: "none",
      // Above the top nav (zIndex 100) so the opaque triangle can mask
      // the top nav's borderBottom underneath the curve, but below the
      // Drawer paper (zIndex 1200) so the expanding sidebar covers it.
      zIndex: 101,
      // Gradient center at local (17, 17) = absolute (76, 80). Radius 16
      // → arc tangents at absolute (60, 80) on the bottom-left and
      // (76, 64) on the top-right. Bottom tangent col 60 lines up with
      // `sideRailRightBorder` (which sits at col 60 — 1px outside the
      // paper — to bypass the paper covering col 59). Top tangent col 76
      // lines up with the top nav's bottom border (`left: 76` in
      // AdminLayout.styles.ts).
      backgroundImage:
        "radial-gradient(circle 16px at 17px 17px, transparent 0, transparent 15px, #DADFF6 15px, #DADFF6 16px, white 16px)",
      [theme.breakpoints.down('md')]: {
        // Mobile uses a temporary drawer + full-width header borderBottom,
        // so there's no L-shaped frame for the curve to soften.
        display: "none",
      },
    },
    // Standalone vertical border that draws the side rail's right edge
    // OUTSIDE the paper (at col 60), 1px to the right of where the paper's
    // own ::after would sit. This places the border at the same column as
    // the cornerCurve's bottom tangent, eliminating the 1px offset the
    // user saw between the curve and the rail. Sits below the Drawer
    // paper (zIndex < 1200) so it's covered by the paper as the drawer
    // expands — at which point the paper's own ::after takes over at the
    // newly-exposed right edge of the expanded panel.
    sideRailRightBorder: {
      position: "fixed",
      // Starts 16px below the desktop header (paper top 64 + curve
      // height 16) so the line begins immediately under the cornerCurve.
      top: 80,
      left: 60,
      width: 1,
      bottom: 0,
      backgroundColor: "#DADFF6",
      pointerEvents: "none",
      zIndex: 100,
      [theme.breakpoints.down('md')]: {
        // Mobile uses a temporary drawer with no permanent rail.
        display: "none",
      },
    },
  }),
);
